import { useCallback, useMemo } from 'react';
import * as ApolloReactHooks from '@apollo/react-hooks';

import {
	DateWithPriceFragment,
	FlightsMinPricesInPeriodRTQuery,
	FlightsMinPricesInPeriodRTQueryVariables,
	useFlightsMinPricesInPeriodRTQuery
} from '@websky/graphql';
import { isRequireCurrencyParam } from '../../../../utils';
import { getCurrency } from '../../../../cache';
import { convertAviaToMinPricesParameters } from '@shared/lib/results/convertAviaToMinPricesParameters';

export const useMinPricesInPeriod = (
	options: ApolloReactHooks.QueryHookOptions<
		FlightsMinPricesInPeriodRTQuery,
		FlightsMinPricesInPeriodRTQueryVariables
	>
) => {
	const optionsClone = {
		...options,
		variables: {
			...options.variables,
			parameters: convertAviaToMinPricesParameters({ ...options.variables.parameters })
		}
	};

	if (isRequireCurrencyParam()) {
		optionsClone.variables.parameters.currency = getCurrency();
	}

	const { data, loading, ...props } = useFlightsMinPricesInPeriodRTQuery(optionsClone);

	const pricesByLeg = useMemo(() => {
		const pricesMap = new Map<
			number,
			{
				datesOW: Map<string, DateWithPriceFragment>;
				datesRT: Map<string, DateWithPriceFragment>;
				hasOWPrices: boolean;
				hasRTPrices: boolean;
			}
		>();

		const legs = data?.FlightsMinPricesInPeriodRT?.datesWithLowestPricesByLegs;
		if (!legs) {
			return pricesMap;
		}

		const processDates = (targetMap: Map<string, DateWithPriceFragment>, dates?: DateWithPriceFragment[]) => {
			if (!dates?.length) {
				return false;
			}

			let hasPrices = false;

			for (const dateWithPrice of dates) {
				const datePriceAmount = dateWithPrice.price?.amount;

				const existDate = targetMap.get(dateWithPrice.date);
				const existPriceAmount = existDate?.price?.amount;

				if (!existDate?.price || datePriceAmount < existPriceAmount) {
					targetMap.set(dateWithPrice.date, dateWithPrice);
				}

				if (dateWithPrice?.price) {
					hasPrices = true;
				}
			}

			return hasPrices;
		};

		legs.forEach(leg => {
			const legId = +leg.legId;
			let pricesLeg = pricesMap.get(legId);

			if (!pricesLeg) {
				pricesLeg = { datesOW: new Map(), datesRT: new Map(), hasOWPrices: false, hasRTPrices: false };
				pricesMap.set(legId, pricesLeg);
			}

			pricesLeg.hasOWPrices = processDates(pricesLeg.datesOW, leg.oneWay);
			pricesLeg.hasRTPrices = processDates(pricesLeg.datesRT, leg.halfRoundTrip);
		});

		return pricesMap;
	}, [data?.FlightsMinPricesInPeriodRT?.datesWithLowestPricesByLegs]);

	const getPrices = useCallback(
		(legId: number, isRT?: boolean) => {
			const legsCount = pricesByLeg.size;

			if (!legsCount) {
				return [];
			}

			const prices = pricesByLeg.get(legId) ?? [...pricesByLeg.values()][0];
			if (!prices) {
				return [];
			}

			// OW min prices
			if (prices.hasOWPrices && !prices.hasRTPrices) {
				return [...prices.datesOW.values()];
			}

			// RT min prices
			if (prices.hasRTPrices && !prices.hasOWPrices) {
				return [...prices.datesRT.values()];
			}

			// Combined RT and OW min prices for RT
			const isActualRT = isRT && legsCount > 1;
			if (isActualRT && prices.hasRTPrices && prices.hasOWPrices) {
				return [...prices.datesOW.entries()].map(([date, owDateWithPrice]) => {
					const rtDateWithPrice = prices.datesRT.get(date);

					return !!rtDateWithPrice?.price ? rtDateWithPrice : owDateWithPrice;
				});
			}

			return [...prices.datesOW.values()];
		},
		[pricesByLeg]
	);

	return {
		data,
		pricesByLeg,
		hasPrices: pricesByLeg.size > 0,
		loading,
		getPrices,
		...props
	};
};
